import { cases } from '../data/cases';

export function shuffle<T>(items: T[]): T[] {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1));
    [next[index], next[swap]] = [next[swap], next[index]];
  }
  return next;
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

/**
 * 新收录案例优先在前；近期跳过的案例放到队尾。
 * fresh 内部、其余案例内部都重新洗牌，因此新案例之间也不会固定顺序。
 */
export function buildDiscoveryQueue(options: { lastHomeCase?: string; skippedCases?: string[] } = {}): string[] {
  const allIds = cases.map((item) => item.id);
  const recentlySkipped = uniqueIds(options.skippedCases ?? []).slice(0, 3);
  const skippedSet = new Set(recentlySkipped);
  const fresh = shuffle(
    cases
      .filter((item) => item.isNew && !skippedSet.has(item.id))
      .map((item) => item.id),
  );
  const rest = shuffle(allIds.filter((id) => !fresh.includes(id) && !skippedSet.has(id)));
  const skippedTail = shuffle(allIds.filter((id) => skippedSet.has(id)));
  let queue = [...fresh, ...rest, ...skippedTail];
  if (queue.length > 1 && queue[0] === options.lastHomeCase) {
    queue = [...queue.slice(1), queue[0]];
  }
  return queue;
}

/**
 * 保留已有队列顺序，同时补齐后来新增的案例。
 * 新增的 fresh 案例放在前部；缺失的旧案例放在尾部。
 */
export function hydrateDiscoveryQueue(queue: string[], skippedCases: string[] = []): string[] {
  const allIds = cases.map((item) => item.id);
  const valid = uniqueIds(queue.filter((id) => allIds.includes(id)));
  const missing = allIds.filter((id) => !valid.includes(id));
  if (missing.length === 0) return valid;
  const missingFresh = shuffle(missing.filter((id) => cases.find((item) => item.id === id)?.isNew));
  const missingRest = shuffle(missing.filter((id) => !cases.find((item) => item.id === id)?.isNew));
  const ordered = [...missingFresh, ...valid, ...missingRest];
  if (ordered.length > 1 && ordered[0] === skippedCases[0]) {
    return [...ordered.slice(1), ordered[0]];
  }
  return ordered;
}

/**
 * 跳过/进入案例后，把当前案例移到队尾。
 * 不会把跳过误记为“看过”。
 */
export function advanceDiscoveryQueue(queue: string[], currentCaseId: string): string[] {
  const withoutCurrent = queue.filter((id) => id !== currentCaseId);
  return [...withoutCurrent, currentCaseId];
}
